import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import api from '@/lib/api';
import { saveTokens } from '@/lib/auth';
import fieldTurfLogo from '@/assets/Group3.png';

function LoginForm({ onSuccess, title = 'Log In' }) {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [showPass, setShowPass] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!formData.email || !formData.password) {
            setError('Please fill in all fields');
            return;
        }

        try {
            setLoading(true);
            // SimpleJWT кастомный вью принимает email + password
            const res = await api.post('/api/auth/login/', {
                email: formData.email,
                password: formData.password,
            });

            const { access, refresh, user } = res.data || {};
            if (!access || !refresh) throw new Error('No tokens in response');

            // сохраняем оба токена
            saveTokens({ access, refresh });

            onSuccess && onSuccess({ user, access, refresh });
        } catch (e) {
            const msg =
                e?.response?.data?.detail ||
                e?.response?.data?.error ||
                e?.message ||
                'Login failed';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[80vh] flex items-center justify-center bg-[#F8F9F2]">
            <div className="bg-white rounded-xl shadow-lg p-8 sm:p-10 w-full max-w-md">
                <div className="text-center mb-8">
                    <img
                        src={fieldTurfLogo}
                        alt="FieldTurf Logo"
                        className="w-full max-w-xs mx-auto h-auto"
                    />
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                    <h1 className="text-2xl font-semibold text-center">{title}</h1>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">
                            {error}
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                            Email
                        </Label>
                        <Input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            disabled={loading}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#038540] focus:ring-2 focus:ring-[#038540]/20 transition-colors"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                            Password
                        </Label>
                        <div className="relative">
                            <Input
                                type={showPass ? 'text' : 'password'}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                                className="w-full pr-10 px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#038540] focus:ring-2 focus:ring-[#038540]/20 transition-colors"
                            />
                            <button
                                type="button"
                                className="absolute right-3 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100"
                                onClick={() => setShowPass((v) => !v)}
                                aria-label={showPass ? 'Hide password' : 'Show password'}
                                disabled={loading}
                            >
                                {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 px-4 text-white font-semibold rounded-lg transition-all duration-200 hover:transform hover:-translate-y-0.5 mt-3"
                        style={{ backgroundColor: '#038540', borderColor: '#038540' }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#026d35';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#038540';
                        }}
                    >
                        {loading ? 'Signing in…' : 'Log In'}
                    </Button>
                </form>
            </div>
        </div>
    );
}

export default LoginForm;